import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider } from 'antd';

import { FormattedMessage } from 'react-intl';
import Pficon from 'components/Pficon';

import messages from 'containers/TemplatePreviewPage/TemplateFlows/ComplexFlow/messages';
import {
  getDefaultVariableValue,
  hasFieldError,
} from 'containers/TemplatePreviewPage/utils';
import classNames from 'classnames';

import { FIELD_TYPE } from 'containers/TemplatePreviewPage/constants';
import TextWithMedia from '../TextWithMedia';
import QuestionHeader from '../QuestionHeader';

const TextWithMediaRepeater = ({
  question,
  onUpdateVariable,
  questionIndex,
  skipTopMargin,
  addButtonText = 'Add another',
  buttonStyle = {},
  buttonClassNames = {},
}) => {
  const {
    value: values = [],
    hasError,
    isPristine,
    body,
    placeholder,
    min: minAnswers = 0,
    max: maxAnswers = 100,
    supportedMediaTypes,
  } = question;

  const [initialQuestionSize, setInitialQuestionSize] = useState();

  useEffect(() => {
    if (!initialQuestionSize && values.length > 0) {
      setInitialQuestionSize(values.length);
    }
  }, [values, initialQuestionSize]);

  const underMinCount = Math.max(minAnswers - values.length, 0);
  const overMaxCount = Math.max(values.length - maxAnswers, 0);

  const missingMessage =
    underMinCount === 1
      ? messages.validation.fieldIncomplete
      : messages.validation.fieldIncompletePlural;

  const extraValuesMessage =
    overMaxCount === 1
      ? messages.validation.fieldHasExtra
      : messages.validation.fieldHasExtraPlural;

  const onUpdate = useCallback(
    (value, updateIndex) => {
      const { text, media } = value;
      const updatedValues = values.map((existingValue, index) => {
        if (index === updateIndex) {
          return { media, text };
        }
        return existingValue;
      });
      onUpdateVariable(updatedValues);
    },
    [onUpdateVariable, values],
  );

  const onUpdateSingleValue = useCallback(
    (index) => (value) => {
      onUpdate(value, index);
    },
    [onUpdate],
  );

  const onAddValue = useCallback(() => {
    onUpdateVariable([
      ...values,
      getDefaultVariableValue({ fieldType: FIELD_TYPE.TEXT_WITH_MEDIA }),
    ]);
  }, [onUpdateVariable, values]);

  const onRemoveValue = useCallback(
    (indexToRemove) => {
      onUpdateVariable(values.filter((_, index) => index !== indexToRemove));
    },
    [onUpdateVariable, values],
  );
  const addDisabled = values.length >= maxAnswers;

  return (
    <React.Fragment>
      <QuestionHeader
        title={question.body}
        note={question.note}
        description={question.description}
        skipTopMargin={questionIndex === 0 && skipTopMargin}
      />
      <ul className="flex flex-col margin-none padding-none">
        {values.map((value = {}, index) => {
          const subQuestion = {
            body,
            fieldType: FIELD_TYPE.TEXT_WITH_MEDIA,
            value,
            placeholder,
            required: true,
            isPristine,
            supportedMediaTypes,
          };
          const subQuestionHasError = hasFieldError(subQuestion);
          const deleteDisabled = values.length <= minAnswers;
          Object.assign(subQuestion, { hasError: subQuestionHasError });
          return (
            <li className="flex flex-items-center margin-btm-sm">
              <TextWithMedia
                question={subQuestion}
                onUpdateVariable={onUpdateSingleValue(index)}
                hideHeader
                hideErrors
                autoFocus={
                  (initialQuestionSize &&
                    values.length > initialQuestionSize) ||
                  (index === 0 && questionIndex === 0)
                }
                onEnter={onAddValue}
                isRepeating
              />
              <Divider
                type="vertical"
                className="vertical-margin-none horizontal-margin-xs background-grey-5"
                style={{ height: 42 }}
              />
              <Button
                disabled={deleteDisabled}
                type="button"
                onClick={() => onRemoveValue(index)}
                style={{ height: 38, lineHeight: '38px' }}
                className={classNames(
                  'outline-blue',
                  'color-grey-10',
                  'text-align-center',
                  'padding-vertical-none',
                  'padding-horizontal-xs',
                  'border-none',
                  'background-transparent',
                  'background-transparent-hover',
                  'shadow-none',
                  {
                    'color-grey-6': deleteDisabled,
                    'color-grey-6-hover': deleteDisabled,
                    'color-grey-10': !deleteDisabled,
                  },
                )}
              >
                <Pficon type="close" className="text-size-h3" />
              </Button>
            </li>
          );
        })}
        {!isPristine && hasError && Boolean(underMinCount) && (
          <li className="display-block margin-btm-sm">
            <span className="color-red-6" key={`${question.id}-validation`}>
              <FormattedMessage
                {...missingMessage}
                values={{
                  missingValuesCount: underMinCount,
                }}
              />
            </span>
          </li>
        )}
        {!isPristine && hasError && Boolean(overMaxCount) && (
          <li className="display-block margin-btm-sm">
            <span
              className="color-red-6"
              key={`${question.id}-validation-count`}
            >
              <FormattedMessage
                {...extraValuesMessage}
                values={{
                  extraValuesCount: overMaxCount,
                }}
              />
            </span>
          </li>
        )}
        {!isPristine && hasError && !(underMinCount || overMaxCount) && (
          <li className="display-block margin-vertical-sm">
            <span className="color-red-6">
              <FormattedMessage {...messages.validation.fieldHasSubError} />
            </span>
          </li>
        )}
        <li className="display-block">
          <Button
            type="button"
            onClick={onAddValue}
            disabled={addDisabled}
            style={buttonStyle}
            className={classNames(
              'color-grey-1',
              'border-none',
              'outline-blue',
              {
                ...buttonClassNames,
                'background-grey-8': addDisabled,
                'background-grey-8-hover': addDisabled,
                'background-grey-10': !addDisabled,
              },
            )}
          >
            {addButtonText}
          </Button>
        </li>
      </ul>
    </React.Fragment>
  );
};

TextWithMediaRepeater.propTypes = {
  question: PropTypes.object,
  questionIndex: PropTypes.number,
  onUpdateVariable: PropTypes.func.isRequired,
  buttonStyle: PropTypes.object,
  buttonClassNames: PropTypes.object,
};

export default TextWithMediaRepeater;
